import { Container } from '@playcanvas/pcui';
import { Entity, Mat4, Quat, TranslateGizmo, Vec3 } from 'playcanvas';

import { estimateSimilarity, isSimilarityError, Point3, SimilarityTransform } from '../align/similarity';
import { EntityTransformOp, MultiOp, PlacePivotOp } from '../edit-ops';
import { Events } from '../events';
import { Pivot } from '../pivot';
import { Scene } from '../scene';
import { Splat } from '../splat';
import { ToolOverlay, OverlayWriter } from '../tool-overlay';
import { Transform } from '../transform';
import { AlignPairLabels } from '../ui/align-pair-labels';

// pointer movement below this many pixels still counts as a click
const CLICK_TOLERANCE = 4;

// screen-space radius for picking an existing point marker
const MARKER_RADIUS = 8;

// viewTransparency applied to the inactive model in 'dim' isolation mode
const DIM_TRANSPARENCY = 0.15;

// a pair's residual counts as an outlier once it exceeds the median by this
// factor — only evaluated once there are enough pairs for a median to mean
// anything (see OUTLIER_MIN_PAIRS)
const OUTLIER_FACTOR = 2;
const OUTLIER_MIN_PAIRS = 4;

const mat = new Mat4();
const invMat = new Mat4();
const q = new Quat();
const qSim = new Quat();
const v = new Vec3();
const w = new Vec3();
const vDelta = new Vec3();
const vScreen = new Vec3();
const t = new Transform();

// a landmark correspondence: the same physical point clicked once on the
// reference model and once on the movable one. stored splat-LOCAL so the
// points ride along when a model is transformed — which keeps every pair
// valid for refining the alignment after it has been applied.
type AlignPair = {
    ref: Vec3 | null;
    mov: Vec3 | null;
};

type AlignPhase = 'models' | 'ref' | 'mov';

// how the model NOT currently expecting a click is presented, so the two
// models can be told apart once they overlap after applying
type AlignIsolation = 'normal' | 'dim' | 'hide';

type AlignSelection = { pairIndex: number, side: 'ref' | 'mov' } | null;

type AlignState = {
    hasModels: boolean;
    sameModels: boolean;
    phase: AlignPhase;
    isolation: AlignIsolation;
    pairs: { hasRef: boolean, hasMov: boolean, complete: boolean, residual: number | null }[];
    completeCount: number;
    selection: AlignSelection;
    rmse: number | null;
    scale: number | null;
    outlierIndex: number;
};

type AlignApplyResult = {
    ok: boolean;
    error?: 'insufficient-points' | 'degenerate' | 'no-models';
    scale?: number;
    rmse?: number;
};

type AlignCancelResult = {
    // the applied transform(s) from this session were undone
    reverted: boolean;
    // there was something to revert (whether or not it succeeded)
    attempted: boolean;
};

class AlignTransformHandler {
    activate() {}
    deactivate() {}
}

// register two splat models from user-clicked landmark pairs: >= 3 pairs
// define a similarity transform (rotation + uniform scale + translation,
// least-squares over all pairs) that is applied to the movable model with
// undo. driven by the align dialog (src/ui/align-dialog.ts), which owns all
// presentation; this tool owns the session, picking and the transform.
class AlignTool {
    activate: () => void;
    deactivate: () => void;
    getFocus: () => { position: Vec3, radius: number } | null;

    constructor(events: Events, scene: Scene, canvasContainer: Container, annotationParent: HTMLElement) {
        let active = false;
        let refSplat: Splat | null = null;
        let movSplat: Splat | null = null;
        const pairs: AlignPair[] = [];
        let selection: AlignSelection = null;
        let isolation: AlignIsolation = 'dim';
        let withScale = true;

        // last solve outcome, indexed like `pairs` (null for incomplete
        // pairs), cleared whenever there aren't enough complete pairs to solve
        let residuals: (number | null)[] | null = null;
        let rmse: number | null = null;
        let scale: number | null = null;

        // cursor bookkeeping for 'Abbrechen': the undo-stack cursor right
        // before this session's first applied transform, plus how many
        // MultiOps this session has added. Cancel only undoes when the
        // cursor is still exactly baseline + count — i.e. nothing else was
        // edited in between — otherwise it would risk undoing the user's
        // unrelated work instead of just this session's alignment.
        let sessionBaselineCursor: number | null = null;
        let sessionAppliedCount = 0;

        const labels = new AlignPairLabels(scene, canvasContainer.dom, annotationParent, 'align-tool-svg');

        const clearSolution = () => {
            residuals = null;
            rmse = null;
            scale = null;
        };

        const firstIncompleteIndex = () => pairs.findIndex(pair => pair.ref === null || pair.mov === null);

        const phase = (): AlignPhase => {
            if (!refSplat || !movSplat || refSplat === movSplat) {
                return 'models';
            }
            const idx = firstIncompleteIndex();
            if (idx === -1) {
                return 'ref';
            }
            return pairs[idx].ref === null ? 'ref' : 'mov';
        };

        // world-space position of a stored splat-local point
        const worldPoint = (splat: Splat, local: Vec3, result: Vec3) => {
            splat.worldTransform.transformPoint(local, result);
        };

        // gathers the world-space correspondences and solves for the
        // similarity transform, without applying anything. shared by the
        // live preview (resolve, below) and the apply button.
        const computeSimilarity = () => {
            if (!refSplat || !movSplat || refSplat === movSplat) {
                return null;
            }
            const src: Point3[] = [];
            const dst: Point3[] = [];
            for (const pair of pairs) {
                if (pair.ref && pair.mov) {
                    worldPoint(movSplat, pair.mov, v);
                    src.push([v.x, v.y, v.z]);
                    worldPoint(refSplat, pair.ref, w);
                    dst.push([w.x, w.y, w.z]);
                }
            }
            return estimateSimilarity(src, dst, { withScale });
        };

        // remaps the solver's residuals (aligned to the complete pairs only)
        // back onto a full pairs-length array so residuals[i] always matches
        // pairs[i], regardless of any incomplete pairs in between
        const commitResult = (result: SimilarityTransform) => {
            const full: (number | null)[] = pairs.map((): number | null => null);
            let k = 0;
            pairs.forEach((pair, i) => {
                if (pair.ref && pair.mov) {
                    full[i] = result.residuals[k++];
                }
            });
            residuals = full;
            rmse = result.rmse;
            scale = result.scale;
        };

        // live preview: recompute after every pairs/model/withScale change.
        // silent on error — fewer than 3 complete pairs is the normal
        // mid-session state, not a mistake worth surfacing (align.apply
        // raises the same errors explicitly when the user asks to apply)
        const resolve = () => {
            const result = computeSimilarity();
            if (!result || isSimilarityError(result)) {
                clearSolution();
                return;
            }
            commitResult(result);
        };

        const computeOutlierIndex = (): number => {
            if (!residuals) {
                return -1;
            }
            const entries: { i: number, r: number }[] = [];
            residuals.forEach((r, i) => {
                if (r !== null) {
                    entries.push({ i, r });
                }
            });
            if (entries.length < OUTLIER_MIN_PAIRS) {
                return -1;
            }
            const sortedR = entries.map(e => e.r).sort((a, b) => a - b);
            const mid = Math.floor(sortedR.length / 2);
            const median = sortedR.length % 2 ? sortedR[mid] : (sortedR[mid - 1] + sortedR[mid]) / 2;
            if (median <= 0) {
                return -1;
            }
            let worst = entries[0];
            for (const e of entries) {
                if (e.r > worst.r) {
                    worst = e;
                }
            }
            return worst.r > median * OUTLIER_FACTOR ? worst.i : -1;
        };

        const state = (): AlignState => ({
            hasModels: !!refSplat && !!movSplat,
            sameModels: !!refSplat && refSplat === movSplat,
            phase: phase(),
            isolation,
            pairs: pairs.map((pair, i) => ({
                hasRef: pair.ref !== null,
                hasMov: pair.mov !== null,
                complete: pair.ref !== null && pair.mov !== null,
                residual: residuals?.[i] ?? null
            })),
            completeCount: pairs.filter(pair => pair.ref !== null && pair.mov !== null).length,
            selection,
            rmse,
            scale,
            outlierIndex: computeOutlierIndex()
        });

        // resets the inactive model back to fully visible/pickable — used
        // whenever the model pairing changes or the tool deactivates, so a
        // dimmed/hidden model never gets stranded that way
        const resetIsolation = () => {
            if (refSplat) {
                refSplat.viewTransparency = 1;
            }
            if (movSplat) {
                movSplat.viewTransparency = 1;
            }
        };

        const applyIsolation = () => {
            if (!refSplat || !movSplat || refSplat === movSplat) {
                resetIsolation();
                return;
            }
            const activeMov = phase() === 'mov';
            const activeSplat = activeMov ? movSplat : refSplat;
            const inactiveSplat = activeMov ? refSplat : movSplat;
            activeSplat.viewTransparency = 1;
            inactiveSplat.viewTransparency = isolation === 'hide' ? 0 : isolation === 'dim' ? DIM_TRANSPARENCY : 1;
            scene.forceRender = true;
        };

        // gizmo for dragging an already-placed point

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);
        const gizmoEntity = new Entity('alignPointGizmo');

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            gizmo.size = camera.ortho ?
                1125 / canvas.clientHeight :
                1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        // world position -> splat-local, writing into result (inverse of worldPoint)
        const localFromWorld = (splat: Splat, world: Vec3, result: Vec3) => {
            invMat.copy(splat.worldTransform).invert();
            invMat.transformPoint(world, result);
        };

        const selectedPoint = (): { splat: Splat, point: Vec3 } | null => {
            if (!selection) {
                return null;
            }
            const pair = pairs[selection.pairIndex];
            const splat = selection.side === 'ref' ? refSplat : movSplat;
            const point = pair?.[selection.side];
            if (!splat || !point) {
                return null;
            }
            return { splat, point };
        };

        // lightweight notify used during a drag: must not touch the gizmo,
        // or re-attaching mid-drag would abort the drag after one move event
        const notify = () => {
            events.fire('align.changed', state());
            scene.forceRender = true;
        };

        // full sync: isolation, gizmo attach/detach, then notify (not
        // drag-safe — re-attaching the gizmo mid-drag would abort the drag)
        const changed = () => {
            applyIsolation();
            gizmo.detach();
            const sel = selectedPoint();
            if (sel) {
                worldPoint(sel.splat, sel.point, v);
                gizmoEntity.setLocalPosition(v);
                gizmo.attach(gizmoEntity);
            } else if (selection) {
                selection = null;
            }
            notify();
        };

        gizmo.on('transform:move', () => {
            const sel = selectedPoint();
            if (!sel) {
                return;
            }
            localFromWorld(sel.splat, gizmoEntity.getLocalPosition(), sel.point);
            resolve();
            notify();
        });

        gizmo.on('transform:end', () => {
            changed();
        });

        events.function('align.state', () => state());

        events.on('align.setModels', (ref: Splat | null, mov: Splat | null) => {
            if (ref === refSplat && mov === movSplat) {
                return;
            }
            resetIsolation();
            refSplat = ref;
            movSplat = mov;
            pairs.length = 0;
            selection = null;
            sessionBaselineCursor = null;
            sessionAppliedCount = 0;
            resolve();
            changed();
        });

        events.on('align.setIsolation', (value: AlignIsolation) => {
            if (value === isolation) {
                return;
            }
            isolation = value;
            changed();
        });

        events.on('align.setWithScale', (value: boolean) => {
            if (value === withScale) {
                return;
            }
            withScale = value;
            resolve();
            changed();
        });

        events.on('align.swap', () => {
            if (!refSplat || !movSplat || refSplat === movSplat) {
                return;
            }
            resetIsolation();
            const tmpSplat = refSplat;
            refSplat = movSplat;
            movSplat = tmpSplat;
            pairs.forEach((pair) => {
                const tmpPoint = pair.ref;
                pair.ref = pair.mov;
                pair.mov = tmpPoint;
            });
            if (selection) {
                selection = { pairIndex: selection.pairIndex, side: selection.side === 'ref' ? 'mov' : 'ref' };
            }
            resolve();
            changed();
        });

        events.on('align.reset', () => {
            pairs.length = 0;
            selection = null;
            resolve();
            changed();
        });

        events.on('align.deletePair', (index: number) => {
            if (index >= 0 && index < pairs.length) {
                pairs.splice(index, 1);
                if (selection) {
                    if (selection.pairIndex === index) {
                        selection = null;
                    } else if (selection.pairIndex > index) {
                        selection = { pairIndex: selection.pairIndex - 1, side: selection.side };
                    }
                }
                resolve();
                changed();
            }
        });

        events.on('align.selectPoint', (pairIndex: number, side: 'ref' | 'mov') => {
            const pair = pairs[pairIndex];
            if (!pair || !pair[side]) {
                return;
            }
            const isSame = selection && selection.pairIndex === pairIndex && selection.side === side;
            selection = isSame ? null : { pairIndex, side };
            changed();
        });

        // a participating model getting removed invalidates the session
        events.on('scene.elementRemoved', (element: any) => {
            if (element === refSplat || element === movSplat) {
                resetIsolation();
                refSplat = null;
                movSplat = null;
                pairs.length = 0;
                selection = null;
                sessionBaselineCursor = null;
                sessionAppliedCount = 0;
                resolve();
                changed();
            }
        });

        // markers: dots on every captured point, a segment per complete pair
        const overlay = new ToolOverlay();
        overlay.provider = (writer: OverlayWriter) => {
            if (!active) {
                labels.hide();
                return;
            }
            labels.show();
            labels.setCount(pairs.length);
            const outlierIndex = computeOutlierIndex();
            for (let i = 0; i < pairs.length; i++) {
                const pair = pairs[i];
                if (pair.ref && refSplat) {
                    worldPoint(refSplat, pair.ref, v);
                    writer.dot(v);
                    const residual = residuals?.[i];
                    labels.setLabel(
                        i, v,
                        residual !== undefined && residual !== null ?
                            `${i + 1} · ${residual < 1 ? `${(residual * 100).toFixed(1)} cm` : `${residual.toFixed(2)} m`}` :
                            `${i + 1}`,
                        i === outlierIndex
                    );
                } else {
                    labels.hideLabel(i);
                }
                if (pair.mov && movSplat) {
                    worldPoint(movSplat, pair.mov, w);
                    writer.dot(w);
                }
                if (pair.ref && pair.mov && refSplat && movSplat) {
                    writer.segment(v, w);
                }
            }
        };

        // pointer handling: a click on an existing marker selects it (for
        // dragging); otherwise a click captures the next landmark on the
        // model the current phase expects. clicking the other model (or
        // empty space) only produces a hint — never a stray point.

        const isPrimary = (e: PointerEvent) => {
            return e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary;
        };

        let clicked = false;
        let clickX = 0;
        let clickY = 0;

        const pointerdown = (e: PointerEvent) => {
            if (!clicked && isPrimary(e)) {
                clicked = true;
                clickX = e.offsetX;
                clickY = e.offsetY;
            }
        };

        const pointermove = (e: PointerEvent) => {
            if (clicked && Math.hypot(e.offsetX - clickX, e.offsetY - clickY) > CLICK_TOLERANCE) {
                clicked = false;
            }
        };

        const pickMarker = (x: number, y: number): AlignSelection => {
            const { camera } = scene;
            const cameraPos = camera.mainCamera.getPosition();
            const cameraFwd = camera.mainCamera.forward;
            for (let i = 0; i < pairs.length; i++) {
                const pair = pairs[i];
                for (const side of ['ref', 'mov'] as const) {
                    const local = pair[side];
                    const splat = side === 'ref' ? refSplat : movSplat;
                    if (!local || !splat) {
                        continue;
                    }
                    const world = side === 'ref' ? v : w;
                    worldPoint(splat, local, world);
                    vDelta.sub2(world, cameraPos);
                    if (vDelta.dot(cameraFwd) <= 0) {
                        continue;  // behind the camera
                    }
                    camera.worldToScreen(world, vScreen);
                    const sx = vScreen.x * canvasContainer.dom.clientWidth;
                    const sy = vScreen.y * canvasContainer.dom.clientHeight;
                    if (Math.abs(sx - x) < MARKER_RADIUS && Math.abs(sy - y) < MARKER_RADIUS) {
                        return { pairIndex: i, side };
                    }
                }
            }
            return null;
        };

        const pointerup = async (e: PointerEvent) => {
            if (!clicked || !isPrimary(e)) {
                return;
            }
            clicked = false;

            const marker = pickMarker(clickX, clickY);
            if (marker) {
                e.preventDefault();
                e.stopPropagation();
                selection = marker;
                changed();
                return;
            }

            const side = phase();
            if (side === 'models') {
                return;
            }
            const expected = side === 'ref' ? refSplat : movSplat;

            e.preventDefault();
            e.stopPropagation();

            // in an isolation mode, exclude the inactive model from picking
            // entirely so a click goes through it onto the expected model
            const candidates = isolation === 'normal' ? undefined : [expected];

            // one depth pick does both jobs: it names the frontmost model
            // (the wrong-model guard) and yields the surface point on it
            const [hit] = await scene.camera.intersectMany([{
                x: clickX / canvasContainer.dom.clientWidth,
                y: clickY / canvasContainer.dom.clientHeight
            }], candidates);

            // the readback is asynchronous: the tool may have been deactivated,
            // or the pair this click was destined for removed, while it was in
            // flight. re-read the state instead of trusting the captured side.
            if (!active) {
                return;
            }
            if (phase() !== side) {
                return;
            }

            if (!hit || hit.splat !== expected) {
                events.fire('align.status', side === 'ref' ? 'wrong-ref' : 'wrong-mov');
                return;
            }

            // splat-local surface point on the expected model
            const local = new Vec3();
            localFromWorld(expected, hit.position, local);

            selection = null;
            const idx = firstIncompleteIndex();
            if (side === 'ref') {
                if (idx === -1) {
                    pairs.push({ ref: local, mov: null });
                } else {
                    pairs[idx].ref = local;
                }
            } else {
                pairs[idx].mov = local;
            }
            resolve();
            changed();
        };

        const deleteSelectedPoint = () => {
            if (!selection) {
                return;
            }
            const pair = pairs[selection.pairIndex];
            if (pair) {
                pair[selection.side] = null;
                if (pair.ref === null && pair.mov === null) {
                    pairs.splice(selection.pairIndex, 1);
                }
            }
            selection = null;
            resolve();
            changed();
        };

        events.on('select.delete', () => {
            if (active) {
                deleteSelectedPoint();
            }
        });

        // solve and apply. returns the outcome for the dialog to present.
        events.function('align.apply', (): AlignApplyResult => {
            if (!refSplat || !movSplat || refSplat === movSplat) {
                return { ok: false, error: 'no-models' };
            }

            const result = computeSimilarity();
            if (!result || isSimilarityError(result)) {
                return { ok: false, error: (result && 'error' in result) ? result.error : 'insufficient-points' };
            }

            // the solved similarity maps current world points of the movable
            // model onto the reference; entity local == world (contentRoot is
            // identity), so the new pose composes in closed form.
            const r = result.rotation;
            mat.setIdentity();
            const data = mat.data;
            for (let row = 0; row < 3; row++) {
                for (let col = 0; col < 3; col++) {
                    data[col * 4 + row] = r[row * 3 + col];
                }
            }
            qSim.setFromMat4(mat);

            const splat = movSplat;
            const oldt = new Transform(
                splat.entity.getLocalPosition(),
                splat.entity.getLocalRotation(),
                splat.entity.getLocalScale()
            );

            const newPos = new Vec3();
            qSim.transformVector(oldt.position, newPos);
            newPos.mulScalar(result.scale);
            newPos.add(new Vec3(result.translation[0], result.translation[1], result.translation[2]));

            const newRot = new Quat();
            newRot.mul2(qSim, oldt.rotation);

            const newScale = oldt.scale.clone().mulScalar(result.scale);

            const newt = new Transform(newPos, newRot, newScale);
            if (!oldt.equalsApprox(newt)) {
                const top = new EntityTransformOp({ splat, oldt, newt });

                // place the pivot at the local frame under the new transform
                // (same recipe as the orient tool) so the gizmo follows
                const pivot = events.invoke('pivot') as Pivot;
                mat.setTRS(newt.position, newt.rotation, newt.scale);
                mat.transformPoint(splat.localFrameOrigin, v);
                q.mul2(newt.rotation, splat.localFrame);
                t.set(v, q, newt.scale);
                const pop = new PlacePivotOp({ pivot, oldt: pivot.transform.clone(), newt: t.clone() });

                if (sessionBaselineCursor === null) {
                    sessionBaselineCursor = (events.invoke('edit.state') as { cursor: number }).cursor;
                }
                sessionAppliedCount++;

                events.fire('edit.add', new MultiOp([top, pop]));
            }

            // the solver residuals equal the post-move pair distances (the
            // transform applied to the model is the transform applied to its
            // points), so they can be shown as-is — no need to resolve() again
            commitResult(result);
            changed();

            return { ok: true, scale: result.scale, rmse: result.rmse };
        });

        // undo this session's applied transform(s), but only if nothing else
        // was edited in between (see sessionBaselineCursor above)
        events.function('align.cancel', (): AlignCancelResult => {
            const attempted = sessionAppliedCount > 0;
            const cursorState = events.invoke('edit.state') as { cursor: number } | undefined;
            const expected = sessionBaselineCursor !== null ? sessionBaselineCursor + sessionAppliedCount : null;
            const reverted = attempted && expected !== null && cursorState?.cursor === expected;
            if (reverted) {
                for (let i = 0; i < sessionAppliedCount; i++) {
                    events.fire('edit.undo');
                }
            }
            sessionBaselineCursor = null;
            sessionAppliedCount = 0;
            return { reverted, attempted };
        });

        // one step back: deselect a selected point, else discard whichever
        // pair is currently incomplete. returns whether anything happened,
        // so the dialog's Escape handler can fall through to a full cancel.
        events.function('align.stepBack', (): boolean => {
            if (selection) {
                selection = null;
                changed();
                return true;
            }
            const idx = firstIncompleteIndex();
            if (idx === -1) {
                return false;
            }
            pairs.splice(idx, 1);
            resolve();
            changed();
            return true;
        });

        // frame the captured points ('f' shortcut)
        this.getFocus = () => {
            const points: Vec3[] = [];
            for (const pair of pairs) {
                if (pair.ref && refSplat) {
                    const point = new Vec3();
                    worldPoint(refSplat, pair.ref, point);
                    points.push(point);
                }
                if (pair.mov && movSplat) {
                    const point = new Vec3();
                    worldPoint(movSplat, pair.mov, point);
                    points.push(point);
                }
            }
            if (points.length === 0) {
                return null;
            }
            const position = new Vec3();
            for (const point of points) {
                position.add(point);
            }
            position.mulScalar(1 / points.length);
            let radius = 0.5;
            for (const point of points) {
                radius = Math.max(radius, point.distance(position));
            }
            return { position, radius: radius * 1.5 };
        };

        const transformHandler = new AlignTransformHandler();

        this.activate = () => {
            active = true;
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);
            events.fire('transformHandler.push', transformHandler);
            scene.add(overlay);
            changed();
        };

        this.deactivate = () => {
            active = false;
            gizmo.detach();
            resetIsolation();
            scene.remove(overlay);
            labels.hide();
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup, true);
            events.fire('transformHandler.pop');
            // the session belongs to the dialog that drove the tool: closing
            // it discards the pairs (any applied transform stays; undo is the
            // way back once the session itself is gone)
            refSplat = null;
            movSplat = null;
            pairs.length = 0;
            selection = null;
            sessionBaselineCursor = null;
            sessionAppliedCount = 0;
            clearSolution();
            changed();
        };
    }
}

export { AlignTool };
export type { AlignState, AlignApplyResult, AlignCancelResult, AlignPhase, AlignIsolation, AlignSelection };
